import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowPathIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { i18nService } from '../../services/i18n';
import {
  buildTerminalWebSocketConnection,
  requestTerminalAccessTicket,
} from '../../services/webSocketAuth';

type TerminalStatus = 'connecting' | 'connected' | 'disconnected' | 'not_running';

interface TerminalPanelProps {
  sessionId: string;
  /** Optional close handler — renders a close button when provided. */
  onClose?: () => void;
  /**
   * Hide the panel's own title bar (status + controls). Use when the host view
   * already provides a shared top bar; controls then float in the top-right
   * corner so the terminal doesn't render a second bar.
   */
  hideHeader?: boolean;
}

function isDarkMode(): boolean {
  return document.documentElement.classList.contains('dark');
}

// Full 16-colour ANSI palettes (VS Code Dark+ / Light+) so colourful CLI output
// (ls, git, grep, prompts, TUIs) renders crisply instead of xterm's washed-out
// defaults.
const DARK_THEME = {
  background: '#1b1b1d',
  foreground: '#d4d4d4',
  cursor: '#d4d4d4',
  cursorAccent: '#1b1b1d',
  selectionBackground: '#264f78',
  black: '#1b1b1d',
  red: '#f14c4c',
  green: '#23d18b',
  yellow: '#e5c07b',
  blue: '#3b8eea',
  magenta: '#d670d6',
  cyan: '#29b8db',
  white: '#d4d4d4',
  brightBlack: '#6b6b6b',
  brightRed: '#f76d6d',
  brightGreen: '#4ee8a4',
  brightYellow: '#f0d59a',
  brightBlue: '#5ca7f0',
  brightMagenta: '#e08ce0',
  brightCyan: '#4ccce6',
  brightWhite: '#ffffff',
};

const LIGHT_THEME = {
  background: '#fbfbfc',
  foreground: '#383a42',
  cursor: '#383a42',
  cursorAccent: '#fbfbfc',
  selectionBackground: '#add6ff',
  black: '#383a42',
  red: '#cd3131',
  green: '#107c10',
  yellow: '#795e26',
  blue: '#0451a5',
  magenta: '#a626a4',
  cyan: '#0598bc',
  white: '#a0a1a7',
  brightBlack: '#a0a1a7',
  brightRed: '#cd3131',
  brightGreen: '#14ce14',
  brightYellow: '#b5870b',
  brightBlue: '#0451a5',
  brightMagenta: '#bc05bc',
  brightCyan: '#0598bc',
  brightWhite: '#000000',
};

/**
 * Interactive terminal for a running E2B sandbox session. Connects to the
 * `/api/cowork/sessions/:id/terminal-ws` WebSocket, which bridges to a PTY
 * inside the sandbox. Keystrokes are sent as binary frames; resize events as
 * a text control message `{type:'resize',cols,rows}`.
 *
 * The xterm instance and its WebSocket share a single effect so they are always
 * created/torn down together — this keeps them consistent under React 18
 * StrictMode's mount → unmount → remount cycle in dev.
 */
const TerminalPanel: React.FC<TerminalPanelProps> = ({ sessionId, onClose, hideHeader }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<TerminalStatus>('connecting');
  const [reconnectKey, setReconnectKey] = useState(0);

  const sendResize = useCallback(() => {
    const term = termRef.current;
    const fit = fitRef.current;
    const ws = wsRef.current;
    if (!term || !fit) return;
    try { fit.fit(); } catch { /* container not measurable yet */ }
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
    }
  }, []);

  // xterm + WebSocket lifecycle, kept in one effect so they never desync.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    setStatus('connecting');

    const term = new Terminal({
      convertEol: true,
      cursorBlink: true,
      cursorStyle: 'bar',
      cursorWidth: 2,
      fontSize: 13,
      fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Monaco, "Courier New", monospace',
      fontWeight: '400',
      fontWeightBold: '600',
      lineHeight: 1.3,
      letterSpacing: 0.4,
      scrollback: 8000,
      smoothScrollDuration: 80,
      theme: isDarkMode() ? DARK_THEME : LIGHT_THEME,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(container);
    try { fit.fit(); } catch { /* ignore */ }
    termRef.current = term;
    fitRef.current = fit;

    let ws: WebSocket | null = null;
    void requestTerminalAccessTicket({
      sessionId,
      token: localStorage.getItem('boclaw_token') || '',
      userId: localStorage.getItem('boclaw_user_id') || '',
    }).then((ticket) => {
      if (disposed) return;
      const connection = buildTerminalWebSocketConnection({
        sessionId,
        ticket,
        protocol: window.location.protocol,
        host: window.location.host,
      });
      ws = new WebSocket(connection.url, connection.protocols);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        if (disposed) return;
        setStatus('connected');
        sendResize();
        term.focus();
      };

      ws.onmessage = (ev: MessageEvent) => {
        if (disposed) return;
        if (typeof ev.data === 'string') {
          // Control / error message from the server.
          try {
            const msg = JSON.parse(ev.data);
            if (msg?.type === 'error' && msg.message === 'session_not_running') {
              setStatus('not_running');
              term.write(`\r\n\x1b[33m${i18nService.t('coworkTerminalNotRunning')}\x1b[0m\r\n`);
            } else if (msg?.type === 'error') {
              setStatus('disconnected');
              term.write(`\r\n\x1b[33m${i18nService.t('coworkTerminalOpenFailed')}\x1b[0m\r\n`);
            }
          } catch { /* ignore non-JSON text */ }
          return;
        }
        term.write(new Uint8Array(ev.data as ArrayBuffer));
      };

      ws.onclose = () => {
        if (!disposed) setStatus((prev) => (prev === 'not_running' ? prev : 'disconnected'));
      };

      ws.onerror = () => {
        if (!disposed) setStatus((prev) => (prev === 'not_running' ? prev : 'disconnected'));
      };
    }).catch(() => {
      if (disposed) return;
      setStatus('disconnected');
      term.write(`\r\n\x1b[33m${i18nService.t('coworkTerminalOpenFailed')}\x1b[0m\r\n`);
    });

    const onDataDisposable = term.onData((data: string) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(new TextEncoder().encode(data));
      }
    });

    return () => {
      disposed = true;
      onDataDisposable.dispose();
      // StrictMode-safe: aborting a still-CONNECTING socket makes the browser log
      // a "connection interrupted" error. Wait for open, then close cleanly.
      if (ws?.readyState === WebSocket.CONNECTING) {
        // 捕获本次连接，避免异步清理回调读取已变化的可空变量。
        const closingSocket = ws;
        closingSocket.addEventListener('open', () => { try { closingSocket.close(); } catch { /* 连接已关闭。 */ } }, { once: true });
      } else if (ws) {
        try { ws.close(); } catch { /* ignore */ }
      }
      try { term.dispose(); } catch { /* ignore */ }
      if (ws && wsRef.current === ws) wsRef.current = null;
      if (termRef.current === term) termRef.current = null;
      if (fitRef.current === fit) fitRef.current = null;
    };
  }, [sessionId, reconnectKey, sendResize]);

  // Refit + notify the PTY when the container resizes.
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => sendResize());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [sendResize]);

  // Keep the xterm theme in sync with the app dark/light mode.
  useEffect(() => {
    const obs = new MutationObserver(() => {
      if (termRef.current) {
        termRef.current.options.theme = isDarkMode() ? DARK_THEME : LIGHT_THEME;
      }
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const handleClear = useCallback(() => termRef.current?.clear(), []);
  const handleReconnect = useCallback(() => setReconnectKey((k) => k + 1), []);

  const statusLabel: Record<TerminalStatus, string> = {
    connecting: i18nService.t('coworkTerminalConnecting'),
    connected: i18nService.t('coworkTerminalTitle'),
    disconnected: i18nService.t('coworkTerminalDisconnected'),
    not_running: i18nService.t('coworkTerminalNotRunning'),
  };
  const statusColor =
    status === 'connected' ? 'bg-green-500'
      : status === 'connecting' ? 'bg-yellow-500'
        : 'bg-gray-400';

  const controls = (
    <>
      <button
        type="button"
        onClick={handleReconnect}
        title={i18nService.t('coworkTerminalReconnect')}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
      >
        <ArrowPathIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={handleClear}
        title={i18nService.t('coworkTerminalClear')}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          title={i18nService.t('close')}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </>
  );

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-[#fbfbfc] dark:bg-[#1b1b1d]">
      {!hideHeader && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <span className={`inline-block w-2 h-2 rounded-full ${statusColor}`} />
            <span className="font-medium">{i18nService.t('coworkTerminalTitle')}</span>
            {status !== 'connected' && <span className="text-gray-400">· {statusLabel[status]}</span>}
          </div>
          <div className="flex items-center gap-1">{controls}</div>
        </div>
      )}
      <div
        ref={containerRef}
        className="terminal-surface flex-1 min-h-0 overflow-hidden pl-3 pr-1.5 pt-2 bg-[#fbfbfc] dark:bg-[#1b1b1d]"
        onClick={() => termRef.current?.focus()}
      />
    </div>
  );
};

export default TerminalPanel;
